import { ECONOMY_URL, ECONOMY_ANON_KEY, ECONOMY_AUTH_KEY } from './economyConfig';

export const ECONOMY_EVENT = 'magisp:economy';
export type EconomySnapshot = Record<string, any> & { user_id: string; economy_version: number; gold: number; collection: any[] };
const inflight = new Map<string, Promise<Response>>();
let refreshing: Promise<any> | undefined;
export function economySession(): any {
  try { return JSON.parse(localStorage.getItem(ECONOMY_AUTH_KEY) || 'null'); } catch { return null; }
}
export function economyAccount(): string | null { return economySession()?.user?.id ?? null; }
export async function economyAccessToken(): Promise<string | null> {
  const session = economySession();
  if (!session?.access_token) return null;
  let expiresAt = session.expires_at;
  if (!expiresAt) { try { expiresAt = JSON.parse(atob(session.access_token.split('.')[1])).exp; } catch { /* refresh below */ } }
  if (expiresAt && expiresAt * 1000 > Date.now() + 60000) return session.access_token;
  if (!session.refresh_token) return session.access_token;
  if (!refreshing) refreshing = (async () => {
    const response = await fetch(`${ECONOMY_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', apikey: ECONOMY_ANON_KEY },
      body: JSON.stringify({ refresh_token: session.refresh_token }),
    });
    if (!response.ok) throw new Error('ログインを更新できませんでした。');
    const renewed = await response.json();
    if (economyAccount() !== session.user.id) throw new Error('ログイン中のアカウントが変わりました。');
    localStorage.setItem(ECONOMY_AUTH_KEY, JSON.stringify(renewed));
    return renewed;
  })().finally(() => { refreshing = undefined; });
  return (await refreshing).access_token;
}
export function acceptEconomySnapshot(snapshot: EconomySnapshot | undefined) {
  if (snapshot && snapshot.user_id === economyAccount() && Number.isSafeInteger(snapshot.economy_version)) {
    window.dispatchEvent(new CustomEvent(ECONOMY_EVENT, { detail: snapshot }));
  }
}

/** Reuse the same operation ID after an uncertain response, including reloads. */
export async function economyFetch(input: string, init: RequestInit = {}): Promise<Response> {
  if (!input.startsWith(`${ECONOMY_URL}/functions/v1/`)) return fetch(input, init);
  const account = economyAccount();
  if (!account) throw new Error('ログインが必要です。');
  const token = await economyAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`); headers.set('apikey', ECONOMY_ANON_KEY);
  headers.set('Content-Type', 'application/json');
  let body = init.body, pendingKey: string | undefined;
  const fingerprint = `${account}:${input}:${init.method ?? 'GET'}:${body ?? ''}`;
  if (init.method === 'POST') {
    const payload = typeof body === 'string' ? JSON.parse(body) : {};
    pendingKey = `magisp:pending:${fingerprint}`;
    const id = payload.request_id ?? sessionStorage.getItem(pendingKey) ?? crypto.randomUUID();
    // If durable retry identity cannot be stored, do not submit the mutation.
    sessionStorage.setItem(pendingKey, id);
    body = JSON.stringify({ ...payload, request_id: id });
  }
  let promise = inflight.get(fingerprint);
  if (!promise) {
    promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(input, { ...init, headers, body, signal: controller.signal });
        if (economyAccount() !== account) throw new Error('ログイン中のアカウントが変わりました。');
        if (response.ok) {
          const data = await response.clone().json();
          if (data.success) acceptEconomySnapshot(data.player);
          if (pendingKey) sessionStorage.removeItem(pendingKey);
        } else if (response.status >= 400 && response.status < 500 && pendingKey) sessionStorage.removeItem(pendingKey);
        return response;
      } finally { clearTimeout(timeout); }
    })().finally(() => { inflight.delete(fingerprint); });
    inflight.set(fingerprint, promise);
  }
  return (await promise).clone();
}
export async function economyRequest(path: string, body?: Record<string, unknown>): Promise<any> {
  const response = await economyFetch(`${ECONOMY_URL}/functions/v1/${path}`, body === undefined ? {} : {
    method: 'POST', body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || '取引を処理できませんでした。');
  return data;
}

export async function startEconomyBattle(kind: 'normal' | 'adventure', npcId?: string) {
  const endpoint = kind === 'normal' ? 'collection' : 'adventure';
  const result = await economyRequest(`${endpoint}/battle/start`, npcId ? { npc_id: npcId } : {});
  sessionStorage.setItem(`magisp:battle:${economyAccount()}:${kind}`, result.battle_id);
  return result.battle_id as string;
}
export function pendingBattleId(kind: 'normal' | 'adventure'): string | null {
  return sessionStorage.getItem(`magisp:battle:${economyAccount()}:${kind}`);
}
